import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/shared";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Bell, BellOff, Loader2, Check, AlertTriangle, Pencil, KeyRound } from "lucide-react";
import { toast } from "sonner";
import {
    pushSupported, pushPermission, getCurrentSubscription,
    subscribeToPush, unsubscribeFromPush,
} from "@/lib/push";

const ROL_LABEL = {
    admin: "Administrador",
    operaciones: "Operaciones",
    recepcion: "Recepción",
    usuario_edificio: "Usuario del edificio",
};

function splitNombre(full) {
    const parts = (full || "").trim().split(/\s+/);
    if (parts.length <= 1) return { nombre: parts[0] || "", apellido: "" };
    return { nombre: parts.slice(0, -1).join(" "), apellido: parts[parts.length - 1] };
}

export default function MiPerfil() {
    const { user, setUser } = useAuth();
    const ue = user?.usuario_edificio;
    const isUE = user?.rol === "usuario_edificio";
    const { nombre, apellido } = splitNombre(ue?.nombre || user?.nombre);
    const isCoworking = ue?.unidad?.piso === "Coworking";

    const [subscribed, setSubscribed] = useState(false);
    const [perm, setPerm] = useState("default");
    const [busy, setBusy] = useState(false);
    const supported = pushSupported();
    const isIOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isStandalone = typeof window !== "undefined" && (
        window.matchMedia?.("(display-mode: standalone)").matches
        || window.navigator?.standalone === true
    );

    // WhatsApp edit state (solo para usuario_edificio)
    const [editingWA, setEditingWA] = useState(false);
    const [waValue, setWaValue] = useState(ue?.whatsapp || "");
    const [savingWA, setSavingWA] = useState(false);

    // Cambio de contraseña
    const [pwActual, setPwActual] = useState("");
    const [pwNueva, setPwNueva] = useState("");
    const [pwConfirm, setPwConfirm] = useState("");
    const [pwError, setPwError] = useState("");
    const [pwOk, setPwOk] = useState(false);
    const [savingPw, setSavingPw] = useState(false);

    const refresh = async () => {
        setPerm(pushPermission());
        if (supported) {
            const sub = await getCurrentSubscription();
            setSubscribed(!!sub);
        }
    };
    useEffect(() => { refresh(); }, []); // eslint-disable-line
    useEffect(() => { setWaValue(ue?.whatsapp || ""); }, [ue?.whatsapp]);

    const handleSubscribe = async () => {
        setBusy(true);
        try {
            await subscribeToPush();
            await refresh();
        } catch (e) {
            toast.error(e?.message || "No se pudo activar las notificaciones.");
            await refresh();
        } finally {
            setBusy(false);
        }
    };

    const handleUnsubscribe = async () => {
        if (!window.confirm("¿Desactivar notificaciones en este dispositivo?")) return;
        setBusy(true);
        try {
            await unsubscribeFromPush();
            await refresh();
        } catch (e) {
            toast.error(e?.message || "No se pudo desactivar.");
        } finally {
            setBusy(false);
        }
    };

    const guardarWhatsApp = async () => {
        setSavingWA(true);
        try {
            const r = await api.patch("/usuarios-edificio/me/whatsapp", { whatsapp: waValue });
            const wa = r.data?.whatsapp ?? waValue;
            // Update auth context + localStorage para que persista
            const nextUser = { ...user, usuario_edificio: { ...(user.usuario_edificio || {}), whatsapp: wa } };
            if (setUser) setUser(nextUser);
            localStorage.setItem("foresta_user", JSON.stringify(nextUser));
            setEditingWA(false);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSavingWA(false);
        }
    };

    const guardarPassword = async (e) => {
        e?.preventDefault?.();
        setPwError("");
        setPwOk(false);
        if (!pwActual || !pwNueva || !pwConfirm) {
            setPwError("Completá los tres campos.");
            return;
        }
        if (pwNueva.length < 6) {
            setPwError("La nueva contraseña debe tener al menos 6 caracteres.");
            return;
        }
        if (pwNueva !== pwConfirm) {
            setPwError("La nueva contraseña y la confirmación no coinciden.");
            return;
        }
        setSavingPw(true);
        try {
            await api.post("/auth/change-password", { actual: pwActual, nueva: pwNueva });
            setPwActual(""); setPwNueva(""); setPwConfirm("");
            setPwOk(true);
            setTimeout(() => setPwOk(false), 3000);
        } catch (err) {
            const status = err?.response?.status;
            if (status === 400) {
                setPwError("La contraseña actual es incorrecta.");
            } else {
                setPwError(formatApiError(err));
            }
        } finally {
            setSavingPw(false);
        }
    };

    const rolLabel = ROL_LABEL[user?.rol] || user?.rol;
    let rolUnidad = null;
    if (isUE) {
        if (isCoworking) rolUnidad = "Coworking";
        else if (ue?.es_master) rolUnidad = "Responsable";
        else rolUnidad = "Autorizado";
    }

    return (
        <div>
            <PageHeader title="Mi perfil" subtitle="Tu cuenta y preferencias de notificaciones." />

            <div className="grid gap-3 max-w-2xl">
                <Card>
                    <CardContent className="p-4 space-y-3">
                        <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Cuenta</p>

                        <div className="space-y-2">
                            {nombre && (
                                <Row label="Nombre" value={nombre} />
                            )}
                            {apellido && (
                                <Row label="Apellido" value={apellido} />
                            )}
                            <Row label="Email" value={user?.email} mono />

                            {isUE ? (
                                <div className="flex items-start justify-between gap-3 py-1.5">
                                    <span className="text-[11px] uppercase tracking-wider text-muted-foreground shrink-0 mt-1.5">WhatsApp</span>
                                    <div className="flex-1 min-w-0">
                                        {editingWA ? (
                                            <div className="flex gap-2 items-center">
                                                <Input
                                                    value={waValue}
                                                    onChange={(e) => setWaValue(e.target.value)}
                                                    placeholder="Ej: 261 555 1234"
                                                    className="h-8 text-sm"
                                                    data-testid="perfil-whatsapp-input"
                                                />
                                                <Button size="sm" onClick={guardarWhatsApp} disabled={savingWA} className="h-8" data-testid="perfil-whatsapp-save">
                                                    {savingWA ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                                                </Button>
                                                <Button size="sm" variant="ghost" onClick={() => { setEditingWA(false); setWaValue(ue?.whatsapp || ""); }} className="h-8">
                                                    Cancelar
                                                </Button>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-2 justify-end">
                                                <span className="text-sm font-mono text-foreground">{ue?.whatsapp || <span className="text-muted-foreground italic">sin cargar</span>}</span>
                                                <Button size="sm" variant="ghost" onClick={() => setEditingWA(true)} className="h-7 w-7 p-0" data-testid="perfil-whatsapp-edit">
                                                    <Pencil className="h-3.5 w-3.5" />
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : null}

                            <Row label="Rol" value={rolLabel} />
                            {isUE && ue?.unidad?.etiqueta && (
                                <Row label="Unidad" value={ue.unidad.etiqueta} />
                            )}
                            {rolUnidad && (
                                <Row label="Rol en la unidad" value={rolUnidad} />
                            )}
                        </div>

                        {!isUE && (
                            <p className="text-[11px] text-muted-foreground border-t border-border pt-3">
                                Para cambiar tus datos pedile al admin que actualice tu cuenta.
                            </p>
                        )}
                        {isUE && (
                            <p className="text-[11px] text-muted-foreground border-t border-border pt-3">
                                Email, unidad y rol los gestiona recepción/operaciones. Vos podés actualizar tu WhatsApp.
                            </p>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">Notificaciones push</p>
                                <p className="font-heading text-base font-bold leading-tight">
                                    {subscribed ? "Notificaciones activadas" : "No activas en este dispositivo"}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1.5 leading-snug">
                                    Te avisamos cuando llegue una visita que cargaste.
                                </p>
                            </div>
                            <div className={`h-9 w-9 rounded-md grid place-items-center shrink-0 ${subscribed ? "bg-success/15 text-success" : "bg-muted text-muted-foreground"}`}>
                                {subscribed ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
                            </div>
                        </div>

                        {!supported && (
                            <div className="mt-3 flex gap-2 items-start text-[12px] bg-muted/40 border border-border rounded-md px-3 py-2">
                                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
                                <p className="text-muted-foreground leading-snug">
                                    Este dispositivo no permite notificaciones desde la app.
                                    {isIOS && !isStandalone && " En iPhone, agregá la app a la pantalla de inicio y abrila desde ahí."}
                                </p>
                            </div>
                        )}

                        {supported && perm === "denied" && (
                            <div className="mt-3 flex gap-2 items-start text-[12px] bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2">
                                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 text-destructive shrink-0" />
                                <p className="text-destructive leading-snug">
                                    Permiso bloqueado. Habilitalo desde la configuración del navegador.
                                </p>
                            </div>
                        )}

                        <div className="mt-4 flex gap-2 flex-wrap">
                            {supported && !subscribed && (
                                <Button
                                    onClick={handleSubscribe}
                                    disabled={busy || perm === "denied"}
                                    data-testid="push-activar"
                                >
                                    {busy
                                        ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Activando…</>
                                        : <><Bell className="h-4 w-4 mr-1.5" />Activar notificaciones</>}
                                </Button>
                            )}
                            {supported && subscribed && (
                                <>
                                    <Button variant="outline" disabled className="cursor-default">
                                        <Check className="h-4 w-4 mr-1.5 text-success" /> Activas
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        onClick={handleUnsubscribe}
                                        disabled={busy}
                                        className="text-muted-foreground"
                                        data-testid="push-desactivar"
                                    >
                                        Desactivar
                                    </Button>
                                </>
                            )}
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <KeyRound className="h-3.5 w-3.5 text-muted-foreground" />
                            <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Cambiar contraseña</p>
                        </div>
                        <form onSubmit={guardarPassword} className="space-y-3">
                            <div className="space-y-1.5">
                                <Label htmlFor="pw-actual" className="text-xs">Contraseña actual</Label>
                                <Input
                                    id="pw-actual"
                                    type="password"
                                    autoComplete="current-password"
                                    value={pwActual}
                                    onChange={(e) => { setPwActual(e.target.value); setPwError(""); setPwOk(false); }}
                                    data-testid="pw-actual"
                                />
                            </div>
                            <div className="grid sm:grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <Label htmlFor="pw-nueva" className="text-xs">Nueva contraseña</Label>
                                    <Input
                                        id="pw-nueva"
                                        type="password"
                                        autoComplete="new-password"
                                        value={pwNueva}
                                        onChange={(e) => { setPwNueva(e.target.value); setPwError(""); setPwOk(false); }}
                                        data-testid="pw-nueva"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="pw-confirm" className="text-xs">Confirmar nueva</Label>
                                    <Input
                                        id="pw-confirm"
                                        type="password"
                                        autoComplete="new-password"
                                        value={pwConfirm}
                                        onChange={(e) => { setPwConfirm(e.target.value); setPwError(""); setPwOk(false); }}
                                        data-testid="pw-confirm"
                                    />
                                </div>
                            </div>
                            {pwError && (
                                <p className="text-[12px] text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2" data-testid="pw-error">
                                    {pwError}
                                </p>
                            )}
                            {pwOk && (
                                <p className="text-[12px] text-success bg-success/10 border border-success/30 rounded-md px-3 py-2" data-testid="pw-ok">
                                    Contraseña actualizada.
                                </p>
                            )}
                            <Button
                                type="submit"
                                disabled={savingPw}
                                data-testid="pw-submit"
                            >
                                {savingPw ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Guardando…</> : "Guardar contraseña"}
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}

function Row({ label, value, mono }) {
    return (
        <div className="flex items-baseline justify-between gap-3 py-1">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground shrink-0">{label}</span>
            <span className={`text-sm text-foreground text-right truncate ${mono ? "font-mono" : ""}`}>{value || "—"}</span>
        </div>
    );
}
